import { Controller } from "@hotwired/stimulus"

// Recherche rapide globale — la « palette » du bandeau.
//
// Remplace le widget jQuery UI maison `catcomplete` (app/javascript/legacy.js,
// supprimé) et les quatre cases à cocher en images PNG de l'en-tête. Ce qui a
// motivé la réécriture plutôt qu'un rhabillage :
//
//   - le menu déroulant s'ouvrait sous un champ large d'un tiers d'écran, sur
//     deux lignes de haut : avec sept homonymes, on ne voyait rien et rien ne
//     les distinguait. Une palette centrée, ancrée en haut, laisse la place
//     d'afficher une seconde ligne par résultat ;
//   - `_renderItem` de jQuery UI construisait la ligne en CONCATÉNANT du HTML
//     autour du libellé renvoyé par le serveur. Ici la surbrillance construit
//     des nœuds (`document.createTextNode` / <mark>), le texte n'est jamais
//     interprété ;
//   - `$.ui.autocomplete` n'annule pas la requête précédente, il ignore
//     seulement sa réponse au moment de l'afficher — ce qui suffisait tant que
//     l'ordre des réponses suivait celui des requêtes. AbortController coupe
//     réellement la requête périmée, et son `catch` distingue l'annulation
//     d'une vraie panne réseau.
//
// L'endpoint est le même (`/search`), au contrat JSON groupé décrit dans
// SearchController.
const CATEGORIES = ["customer", "organisation", "student", "request"]

// L'icône est une décision de rendu : elle ne descend plus du serveur (c'était
// `img_category`, une URL d'asset PNG répétée sur chaque ligne). Font Awesome
// 4.7, la police déjà chargée par l'application.
const ICONS = {
  customer: "fa-user",
  organisation: "fa-university",
  student: "fa-graduation-cap",
  request: "fa-file-text-o"
}

// Assez court pour suivre la frappe, assez long pour qu'une saisie continue ne
// parte pas en requête à chaque lettre. La recherche tient en ~18 ms côté
// serveur depuis les index trigrammes.
const DEBOUNCE_MS = 200

const CATEGORIES_KEY = "quickSearch.categories"
const RECENT_KEY = "quickSearch.recent"
const RECENT_MAX = 5

// Réplique côté affichage de la normalisation SQL (`educom_unaccent(lower(…))`) :
// la recherche est insensible à la casse ET aux accents, la surbrillance doit
// l'être aussi, sinon « dupont » trouve « Dupônt » sans rien y surligner.
//
// La normalisation se fait CARACTÈRE PAR CARACTÈRE, et chaque caractère en
// produit exactement un : les positions trouvées dans la chaîne repliée sont
// alors valides telles quelles dans la chaîne d'origine, qui est la seule
// affichée. Un `str.normalize("NFD").replace(…)` global décalerait tout dès le
// premier accent (é → e, deux unités devenues une).
function fold(text) {
  let folded = ""

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index].toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    // "" (une marque combinante isolée) ou plusieurs unités (İ → i + point) :
    // on garde une unité par unité, coûte que coûte.
    folded += char.length === 1 ? char : (char.charAt(0) || "\u0000")
  }

  return folded
}

export default class extends Controller {
  static targets = ["trigger", "shortcut", "modal", "input", "spinner", "chip", "results", "allResults"]
  static values = { url: String, i18n: Object }

  connect() {
    this.modal = window.bootstrap.Modal.getOrCreateInstance(this.modalTarget)
    this.options = []
    this.activeIndex = -1
    this.pending = null
    this.timer = null

    this.shortcutTarget.textContent = this.isApplePlatform() ? "⌘K" : "Ctrl K"
    this.shortcutTarget.hidden = false

    this.restoreCategories()
    this.updateAllResultsLink()
    this.renderIdle()

    this.onDocumentKeydown = this.onDocumentKeydown.bind(this)
    this.onShown = this.onShown.bind(this)
    this.onHidden = this.onHidden.bind(this)
    this.onResultsClick = this.onResultsClick.bind(this)

    document.addEventListener("keydown", this.onDocumentKeydown)
    this.modalTarget.addEventListener("shown.bs.modal", this.onShown)
    this.modalTarget.addEventListener("hidden.bs.modal", this.onHidden)
    this.resultsTarget.addEventListener("click", this.onResultsClick)
  }

  disconnect() {
    document.removeEventListener("keydown", this.onDocumentKeydown)
    this.modalTarget.removeEventListener("shown.bs.modal", this.onShown)
    this.modalTarget.removeEventListener("hidden.bs.modal", this.onHidden)
    this.resultsTarget.removeEventListener("click", this.onResultsClick)
    this.abort()
    clearTimeout(this.timer)
    if (this.modal) this.modal.dispose()
  }

  // ── Ouverture / fermeture ─────────────────────────────────────────────────

  open() {
    this.modal.show()
  }

  onShown() {
    // Le piège à focus de Bootstrap 5 ramène le focus sur la boîte à la fin de
    // l'animation : mettre le champ au point AVANT se le ferait voler.
    this.inputTarget.focus()
    this.inputTarget.select()
    if (this.inputTarget.value.trim() === "") this.renderIdle()
  }

  // `Esc` est géré par Bootstrap, qui ferme et déclenche cet événement : le
  // focus revient au déclencheur, d'où l'utilisateur l'a laissé.
  onHidden() {
    this.abort()
    clearTimeout(this.timer)
    this.setBusy(false)
    this.triggerTarget.focus()
  }

  // ── Raccourcis globaux ────────────────────────────────────────────────────

  onDocumentKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
      event.preventDefault()
      if (this.modalTarget.classList.contains("show")) this.modal.hide()
      else this.open()
      return
    }

    // « / » n'est un raccourci que hors d'un champ de saisie : ailleurs, c'est
    // une barre oblique que l'utilisateur veut taper.
    if (event.key === "/" && !event.metaKey && !event.ctrlKey && !event.altKey &&
        !this.modalTarget.classList.contains("show") && !this.isTypingTarget(event.target)) {
      event.preventDefault()
      this.open()
    }
  }

  isTypingTarget(element) {
    if (!element || !element.tagName) return false
    if (element.isContentEditable) return true

    return ["INPUT", "TEXTAREA", "SELECT", "TRIX-EDITOR"].includes(element.tagName)
  }

  isApplePlatform() {
    const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent

    return /Mac|iPhone|iPad|iPod/i.test(platform)
  }

  // ── Recherche ─────────────────────────────────────────────────────────────

  search() {
    clearTimeout(this.timer)
    const term = this.inputTarget.value

    if (term.trim() === "") {
      this.abort()
      this.setBusy(false)
      this.updateAllResultsLink()
      this.renderIdle()
      return
    }

    this.updateAllResultsLink()
    this.timer = setTimeout(() => this.run(term), DEBOUNCE_MS)
  }

  toggleCategory() {
    this.persistCategories()
    this.updateAllResultsLink()

    if (this.inputTarget.value.trim() === "") this.renderIdle()
    else this.run(this.inputTarget.value)
  }

  async run(term) {
    // LE point du redesign : la requête précédente est ANNULÉE, pas seulement
    // oubliée. Sans cela, une réponse lente partie d'une frappe ancienne
    // arrivait après une réponse rapide plus récente et écrasait l'affichage
    // avec des résultats périmés.
    this.abort()
    const pending = new AbortController()
    this.pending = pending
    this.setBusy(true)

    const params = new URLSearchParams({ term })
    CATEGORIES.forEach((category) => {
      params.set(`filter_${category}`, this.isCategoryActive(category) ? "1" : "0")
    })

    try {
      const response = await fetch(`${this.urlValue}?${params}`, {
        signal: pending.signal,
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" }
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const payload = await response.json()
      if (pending.signal.aborted) return

      this.renderResults(payload)
    } catch (error) {
      // Une annulation n'est pas une panne : elle est provoquée par la frappe
      // suivante, qui affichera son propre résultat.
      if (error.name === "AbortError") return

      this.renderError()
    } finally {
      if (this.pending === pending) {
        this.pending = null
        this.setBusy(false)
      }
    }
  }

  abort() {
    if (!this.pending) return

    this.pending.abort()
    this.pending = null
  }

  setBusy(busy) {
    this.spinnerTarget.hidden = !busy
  }

  // ── Rendu ─────────────────────────────────────────────────────────────────

  renderResults(payload) {
    const tokens = (payload.term || "").split(/\s+/).filter(Boolean)
    const groups = payload.groups || []

    this.updateCounts(groups)
    this.resetList()

    const rendered = groups.filter((group) => group.results.length > 0)
    if (rendered.length === 0) {
      this.renderEmpty(payload.term)
      return
    }

    rendered.forEach((group) => {
      const section = document.createElement("div")
      section.className = "quick-search-group"
      section.setAttribute("role", "group")
      section.setAttribute("aria-label", group.label)

      const heading = document.createElement("div")
      heading.className = "quick-search-group__label"
      heading.textContent = group.label
      section.appendChild(heading)

      group.results.forEach((result) => {
        const option = this.buildOption(result, tokens)
        this.options.push(option)
        section.appendChild(option)
      })

      this.resultsTarget.appendChild(section)
    })

    this.inputTarget.setAttribute("aria-expanded", "true")
    // La première ligne est active d'emblée : ↵ ouvre le résultat le plus
    // probable sans un seul appui sur ↓.
    this.setActive(0)
  }

  buildOption(result, tokens) {
    const option = document.createElement("a")
    option.className = "quick-search-option"
    option.href = result.url
    option.id = `quick_search_option_${result.type}_${result.id}`
    option.setAttribute("role", "option")
    option.setAttribute("aria-selected", "false")

    const icon = document.createElement("i")
    icon.className = `fa ${ICONS[result.type] || "fa-circle-o"} quick-search-option__icon`
    icon.setAttribute("aria-hidden", "true")
    option.appendChild(icon)

    const text = document.createElement("span")
    text.className = "quick-search-option__text"

    const title = document.createElement("span")
    title.className = "quick-search-option__title"
    this.appendHighlighted(title, result.title || "", tokens)
    text.appendChild(title)

    if (result.subtitle) {
      const subtitle = document.createElement("span")
      subtitle.className = "quick-search-option__subtitle"
      subtitle.textContent = result.subtitle
      text.appendChild(subtitle)
    }

    option.appendChild(text)

    return option
  }

  // Le titre est du texte venu de la base : il est posé en nœuds texte, jamais
  // en innerHTML. Un client nommé « <script> » ne peut donc rien exécuter.
  appendHighlighted(node, text, tokens) {
    let cursor = 0

    this.matchRanges(text, tokens).forEach(([start, end]) => {
      if (start > cursor) node.appendChild(document.createTextNode(text.slice(cursor, start)))
      const mark = document.createElement("mark")
      mark.className = "quick-search-mark"
      mark.textContent = text.slice(start, end)
      node.appendChild(mark)
      cursor = end
    })

    if (cursor < text.length) node.appendChild(document.createTextNode(text.slice(cursor)))
  }

  // Positions de CHAQUE mot de la saisie dans le titre, fusionnées : deux mots
  // qui se chevauchent (« ali » et « lain ») ne doivent pas produire deux
  // <mark> imbriqués ni deux tranches qui se recouvrent.
  matchRanges(text, tokens) {
    const haystack = fold(text)
    const ranges = []

    tokens.forEach((token) => {
      const needle = fold(token)
      if (needle.length === 0) return

      let index = haystack.indexOf(needle)
      while (index !== -1) {
        ranges.push([index, index + needle.length])
        index = haystack.indexOf(needle, index + needle.length)
      }
    })

    ranges.sort((left, right) => left[0] - right[0])

    return ranges.reduce((merged, range) => {
      const last = merged[merged.length - 1]
      if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1])
      else merged.push([range[0], range[1]])

      return merged
    }, [])
  }

  renderIdle() {
    this.resetList()
    this.clearCounts()

    if (!CATEGORIES.some((category) => this.isCategoryActive(category))) {
      this.resultsTarget.appendChild(this.stateNode(this.i18nValue.no_category, "fa-filter"))
      return
    }

    const recent = this.recentTerms()
    if (recent.length === 0) {
      this.resultsTarget.appendChild(this.stateNode(this.i18nValue.idle, "fa-lightbulb-o"))
      return
    }

    const section = document.createElement("div")
    section.className = "quick-search-group"

    const heading = document.createElement("div")
    heading.className = "quick-search-group__label"
    heading.textContent = this.i18nValue.recent

    const clear = document.createElement("button")
    clear.type = "button"
    clear.className = "quick-search-group__action"
    clear.textContent = this.i18nValue.recent_clear
    clear.addEventListener("click", () => {
      this.writeStorage(RECENT_KEY, "[]")
      this.renderIdle()
    })
    heading.appendChild(clear)
    section.appendChild(heading)

    recent.forEach((term) => {
      const entry = document.createElement("button")
      entry.type = "button"
      entry.className = "quick-search-recent"

      const icon = document.createElement("i")
      icon.className = "fa fa-history quick-search-option__icon"
      icon.setAttribute("aria-hidden", "true")
      entry.appendChild(icon)
      entry.appendChild(document.createTextNode(term))

      entry.addEventListener("click", () => {
        this.inputTarget.value = term
        this.inputTarget.focus()
        this.updateAllResultsLink()
        this.run(term)
      })
      section.appendChild(entry)
    })

    this.resultsTarget.appendChild(section)
  }

  renderEmpty(term) {
    const node = this.stateNode(this.i18nValue.empty.replace("%{term}", term), "fa-search")

    const hint = document.createElement("span")
    hint.className = "quick-search-state__hint"
    hint.textContent = this.i18nValue.empty_hint
    node.appendChild(hint)

    this.resultsTarget.appendChild(node)
  }

  renderError() {
    this.resetList()
    this.clearCounts()
    this.resultsTarget.appendChild(this.stateNode(this.i18nValue.error, "fa-exclamation-triangle"))
  }

  stateNode(message, iconClass) {
    const node = document.createElement("p")
    node.className = "quick-search-state"
    node.setAttribute("role", "presentation")

    const icon = document.createElement("i")
    icon.className = `fa ${iconClass} quick-search-state__icon`
    icon.setAttribute("aria-hidden", "true")
    node.appendChild(icon)
    node.appendChild(document.createTextNode(message))

    return node
  }

  // Vide la liste ET l'état clavier qui va avec : un `aria-activedescendant`
  // qui pointe vers un nœud disparu est pire que pas d'annonce du tout.
  resetList() {
    this.resultsTarget.replaceChildren()
    this.options = []
    this.activeIndex = -1
    this.inputTarget.removeAttribute("aria-activedescendant")
    this.inputTarget.setAttribute("aria-expanded", "false")
  }

  // ── Navigation clavier ────────────────────────────────────────────────────

  keydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.move(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.move(-1)
        break
      case "Enter": {
        event.preventDefault()
        const option = this.options[this.activeIndex]
        this.rememberTerm(this.inputTarget.value)
        if (option) window.location.assign(option.href)
        else if (this.inputTarget.value.trim() !== "") this.allResultsTarget.click()
        break
      }
      default:
        break
    }
  }

  // Les groupes ne sont qu'un habillage : la liste traversée est plate, ↓ sur
  // la dernière ligne d'une catégorie passe à la première de la suivante.
  move(step) {
    if (this.options.length === 0) return

    this.setActive((this.activeIndex + step + this.options.length) % this.options.length)
  }

  setActive(index) {
    this.options.forEach((option, position) => {
      const active = position === index
      option.classList.toggle("is-active", active)
      option.setAttribute("aria-selected", active ? "true" : "false")
    })

    this.activeIndex = index
    const option = this.options[index]
    if (!option) return

    // `nearest` ne bouge que si la ligne sort du cadre — pas de saut à chaque ↓.
    option.scrollIntoView({ block: "nearest" })
    this.inputTarget.setAttribute("aria-activedescendant", option.id)
  }

  onResultsClick(event) {
    if (!event.target.closest("a.quick-search-option")) return

    this.rememberTerm(this.inputTarget.value)
  }

  // ── Chips de filtre ───────────────────────────────────────────────────────

  isCategoryActive(category) {
    const chip = this.chipTargets.find((element) => element.dataset.category === category)

    return chip ? chip.checked : false
  }

  restoreCategories() {
    const stored = this.readStorage(CATEGORIES_KEY)
    if (!stored) return

    let saved
    try {
      saved = JSON.parse(stored)
    } catch {
      return
    }
    if (!Array.isArray(saved)) return

    this.chipTargets.forEach((chip) => { chip.checked = saved.includes(chip.dataset.category) })
  }

  persistCategories() {
    const active = this.chipTargets.filter((chip) => chip.checked).map((chip) => chip.dataset.category)

    this.writeStorage(CATEGORIES_KEY, JSON.stringify(active))
  }

  updateCounts(groups) {
    this.clearCounts()

    groups.forEach((group) => {
      const badge = this.countElement(group.category)
      if (!badge) return

      badge.textContent = group.total
      badge.hidden = false
    })
  }

  clearCounts() {
    CATEGORIES.forEach((category) => {
      const badge = this.countElement(category)
      if (!badge) return

      badge.textContent = ""
      badge.hidden = true
    })
  }

  countElement(category) {
    return this.element.querySelector(`[data-quick-search-count="${category}"]`)
  }

  // ── Page de résultats et historique ───────────────────────────────────────

  // Le lien du pied de palette mène à la page de résultats existante, avec la
  // saisie et les filtres courants — c'est l'ancien formulaire du bandeau,
  // réduit à son URL.
  updateAllResultsLink() {
    const params = new URLSearchParams({ term: this.inputTarget.value, result_page: "1" })
    CATEGORIES.forEach((category) => {
      params.set(`filter_${category}`, this.isCategoryActive(category) ? "1" : "0")
    })

    this.allResultsTarget.href = `${this.urlValue}?${params}`
  }

  // Mémorisé au moment d'OUVRIR un résultat, pas à la frappe : sinon
  // l'historique se remplirait des préfixes successifs d'une même recherche.
  rememberTerm(term) {
    const trimmed = term.trim()
    if (trimmed === "") return

    const terms = [trimmed, ...this.recentTerms().filter((entry) => entry !== trimmed)]
    this.writeStorage(RECENT_KEY, JSON.stringify(terms.slice(0, RECENT_MAX)))
  }

  recentTerms() {
    const stored = this.readStorage(RECENT_KEY)
    if (!stored) return []

    try {
      const terms = JSON.parse(stored)

      return Array.isArray(terms) ? terms.filter((term) => typeof term === "string") : []
    } catch {
      return []
    }
  }

  // localStorage lève en navigation privée sur certains navigateurs, et quand
  // le quota est atteint : la recherche doit continuer de fonctionner sans.
  readStorage(key) {
    try {
      return window.localStorage.getItem(key)
    } catch {
      return null
    }
  }

  writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value)
    } catch {
      // Sans mémoire, on repart des valeurs par défaut à chaque ouverture.
    }
  }
}
